import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { useQuery } from '@apollo/react-hooks'
import { useTranslation } from 'react-i18next'
import {
  Card,
  CardBody,
  Alert,
  Button,
} from '@/components/ui-compat'
import { Plus, X, Building, Layers, Maximize2, Minimize2, Upload, Settings, ArrowRight } from 'lucide-react'
import { Button as ShadcnButton } from '@/components/ui/button'
import { ME, BUILDINGS, SNAGS_BY_FLOOR, SNAG_SETTINGS } from '../../../../api/gql/query'
import useSnagsFilters, { ALL_STATUSES, ALL_PRIORITIES } from '../../../../hooks/useSnagsFilters'
import useRouter from '../../../../hooks/useRouter'
import { useModuleActions } from '../../../../hooks/useMyPermissions'
import SnagsFilterBar from '../SnagsFilterBar'
import BlueprintCanvas from './BlueprintCanvas'
import BlueprintUploadModal from './BlueprintUploadModal'
import SnagModal from '../SnagModal'

interface Building {
  id: string
  name: string
  code: string | null
  floors: Floor[]
}

interface Floor {
  id: string
  name: string
  level: number
  blueprintFile: {
    id: string
    filename: string
    key: string
  } | null
  building: {
    id: string
    name: string
    code: string | null
  }
}

interface Snag {
  id: string
  number: number
  displayNumber: string
  title: string
  status: string
  priority: string
  pinX: number | null
  pinY: number | null
  infrastructureType: string | null
}

const BlueprintView: React.FC = () => {
  const { t } = useTranslation()
  const { history, match, query } = useRouter()
  const { data: meData } = useQuery(ME)
  const currentProject = meData?.me?.currentProject
  const projectId = currentProject?.id

  const { canCreate, canAdmin } = useModuleActions('ISSUES', projectId)

  const [isPlacementMode, setIsPlacementMode] = useState(false)
  const [selectedSnagId, setSelectedSnagId] = useState<string | null>(null)
  const [showSnagModal, setShowSnagModal] = useState(false)
  const [newSnagPosition, setNewSnagPosition] = useState<{ x: number; y: number } | null>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [editingPinSnagId, setEditingPinSnagId] = useState<string | null>(null) // Track snag being edited for pin location
  const [pendingPinCoordinates, setPendingPinCoordinates] = useState<{ x: number; y: number } | null>(null) // Pending pin coordinates to be saved via modal
  const [focusPin, setFocusPin] = useState<{ x: number; y: number } | null>(null) // Read-only viewport focus target (Task 4b)

  // Check for editPinSnagId URL param (coming from list view)
  const editPinSnagIdFromUrl = query.editPinSnagId as string | undefined

  // Check for focusSnagId URL param (coming from the "show on blueprint" preview click)
  const focusSnagIdFromUrl = query.focusSnagId as string | undefined

  // Clear the URL param helper
  const clearEditPinSnagIdParam = useCallback(() => {
    const params = new URLSearchParams(window.location.search)
    params.delete('editPinSnagId')
    history.replace(`${match.url}?${params.toString()}`)
  }, [history, match.url])

  const clearFocusSnagIdParam = useCallback(() => {
    const params = new URLSearchParams(window.location.search)
    params.delete('focusSnagId')
    history.replace(`${match.url}?${params.toString()}`)
  }, [history, match.url])

  // Auto-enable placement mode when editPinSnagId is in URL
  useEffect(() => {
    if (editPinSnagIdFromUrl && !editingPinSnagId && !isPlacementMode) {
      setEditingPinSnagId(editPinSnagIdFromUrl)
      setIsPlacementMode(true)
      clearEditPinSnagIdParam()
    }
  }, [editPinSnagIdFromUrl, editingPinSnagId, isPlacementMode, clearEditPinSnagIdParam])

  // Handle Escape key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false)
      }
    }

    if (isFullscreen) {
      document.addEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [isFullscreen])

  // Fetch buildings with floors
  const { data: buildingsData, loading: buildingsLoading, error: buildingsError, refetch: refetchBuildings } = useQuery(
    BUILDINGS,
    {
      variables: { projectId },
      skip: !projectId,
    }
  )

  // Fetch snag settings for infrastructure types
  const { data: settingsData } = useQuery(SNAG_SETTINGS, {
    variables: { projectId },
    skip: !projectId,
  })

  const buildings: Building[] = buildingsData?.buildings || []
  const infrastructureTypes: string[] = settingsData?.snagSettings?.infrastructureTypes || []

  // URL-persisted filters (shared with list view)
  const [filters, setFilters] = useSnagsFilters({
    defaultInfrastructureTypes: infrastructureTypes,
  })

  // Auto-select first building and floor if none selected
  useEffect(() => {
    if (buildings.length > 0 && !filters.floorId) {
      const firstBuilding = buildings[0]
      if (firstBuilding.floors.length > 0) {
        setFilters({
          ...filters,
          buildingId: firstBuilding.id,
          floorId: firstBuilding.floors[0].id,
        })
      }
    }
  }, [buildings])

  // Fetch snags for selected floor
  const { data: snagsData, loading: snagsLoading, refetch: refetchSnags } = useQuery(
    SNAGS_BY_FLOOR,
    {
      variables: { floorId: filters.floorId },
      skip: !filters.floorId,
    }
  )

  const snags: Snag[] = snagsData?.snagsByFloor || []

  // Auto-focus the blueprint viewport when focusSnagId is in URL (coming from the
  // pin preview click in SnagModal / SnagsList). The snag list loads async, so wait
  // until it appears in `snags` before acting; only then clear the param.
  useEffect(() => {
    if (!focusSnagIdFromUrl) return
    const target = snags.find((s) => s.id === focusSnagIdFromUrl)
    if (!target || target.pinX == null || target.pinY == null) return
    setSelectedSnagId(target.id)
    setFocusPin({ x: target.pinX, y: target.pinY })
    clearFocusSnagIdParam()
  }, [focusSnagIdFromUrl, snags, clearFocusSnagIdParam])

  // Clear a stale focus target when the floor actually CHANGES: the viewer's fit
  // effect re-runs whenever OpenSeadragonViewer is recreated, so a leftover
  // focusPin would re-fire the viewport jump onto a pin from a different floor.
  //
  // It must not fire on mount. Effects run in declaration order, so a plain
  // [filters.floorId] effect ran right after the focus effect above and wiped the
  // pin it had just set — whenever the Apollo cache was warm enough to have the
  // snags ready in the first render, which is every visit after the first one.
  const focusedFloorRef = useRef(filters.floorId)
  useEffect(() => {
    if (focusedFloorRef.current === filters.floorId) return
    focusedFloorRef.current = filters.floorId
    setFocusPin(null)
  }, [filters.floorId])

  // Find selected floor
  const selectedFloor = useMemo(() => {
    if (!filters.floorId) return null
    for (const building of buildings) {
      const floor = building.floors.find((f) => f.id === filters.floorId)
      if (floor) return floor
    }
    return null
  }, [buildings, filters.floorId])

  // Filter snags by all filter criteria
  // Empty array = show all, has values = only show those values (INCLUDED)
  const visibleSnags = useMemo(() => {
    let filtered = snags

    // Filter by status (empty = show all)
    if (filters.statuses.length > 0) {
      filtered = filtered.filter((snag) => filters.statuses.includes(snag.status as any))
    }

    // Filter by priority (empty = show all)
    if (filters.priorities.length > 0) {
      filtered = filtered.filter((snag) => filters.priorities.includes(snag.priority as any))
    }

    // Filter by infrastructure type (empty = show all, values = INCLUDED)
    if (filters.infrastructureTypes.length > 0) {
      filtered = filtered.filter(
        (snag) => snag.infrastructureType && filters.infrastructureTypes.includes(snag.infrastructureType)
      )
    }

    // Filter by search query
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase()
      filtered = filtered.filter(
        (snag) =>
          snag.title.toLowerCase().includes(query) ||
          snag.displayNumber.toLowerCase().includes(query)
      )
    }

    return filtered
  }, [snags, filters])

  // Count snags by status for stats display
  const snagStats = useMemo(() => {
    const stats = { total: snags.length, visible: visibleSnags.length, open: 0, inProgress: 0, resolved: 0 }
    snags.forEach((s) => {
      if (s.status === 'OPEN' || s.status === 'REOPENED') stats.open++
      else if (s.status === 'IN_PROGRESS') stats.inProgress++
      else if (s.status === 'RESOLVED' || s.status === 'VERIFIED' || s.status === 'CLOSED') stats.resolved++
    })
    return stats
  }, [snags, visibleSnags])

  // Get sorted list of visible snag IDs for navigation (sorted by snag number)
  const filteredSnagIds = useMemo(() => {
    return [...visibleSnags]
      .sort((a, b) => a.number - b.number)
      .map((snag) => snag.id)
  }, [visibleSnags])

  const handleMarkerClick = (snagId: string) => {
    setSelectedSnagId(snagId)
    setShowSnagModal(true)
  }

  const handlePlaceMarker = async (position: { x: number; y: number }) => {
    setIsPlacementMode(false)

    // If we're editing an existing snag's pin location
    if (editingPinSnagId) {
      const snagIdToEdit = editingPinSnagId
      setEditingPinSnagId(null)

      // Store pending coordinates - will be saved when user clicks Update in modal
      setPendingPinCoordinates(position)
      setSelectedSnagId(snagIdToEdit)
      setShowSnagModal(true)
    } else {
      // Creating a new snag
      setNewSnagPosition(position)
      setShowSnagModal(true)
    }
  }

  const handleAddSnag = () => {
    setSelectedSnagId(null)
    setNewSnagPosition(null)
    setEditingPinSnagId(null)
    setIsPlacementMode(true)
  }

  const handleChangePinLocation = (snagId: string, _floorId: string) => {
    // Close modal and enable placement mode for the existing snag
    setShowSnagModal(false)
    setEditingPinSnagId(snagId)
    setSelectedSnagId(null)
    setNewSnagPosition(null)
    setIsPlacementMode(true)
  }

  // Show-on-blueprint from the pin preview inside this view's own SnagModal:
  // close the modal and re-center the (already visible) blueprint on the pin —
  // no navigation needed since we're already on this floor's blueprint tab.
  const handleShowOnBlueprint = (snagId: string) => {
    setShowSnagModal(false)
    setSelectedSnagId(snagId)
    setPendingPinCoordinates(null)
    setNewSnagPosition(null)
    const s = snags.find((x) => x.id === snagId)
    if (s?.pinX != null && s?.pinY != null) setFocusPin({ x: s.pinX, y: s.pinY })
  }

  const handleSnagModalClose = (saved?: boolean) => {
    setShowSnagModal(false)
    setSelectedSnagId(null)
    setNewSnagPosition(null)
    setPendingPinCoordinates(null)
    if (saved) {
      refetchSnags()
    }
  }

  const handleNavigateSnag = (snagId: string) => {
    setSelectedSnagId(snagId)
  }

  // Blueprint upload success handler
  const handleUploadSuccess = useCallback(async () => {
    if (refetchBuildings) {
      await refetchBuildings()
    }
  }, [refetchBuildings])

  if (buildingsError) {
    return (
      <Alert color="danger">
        {t('Common_error')}: {buildingsError.message}
      </Alert>
    )
  }

  if (buildings.length === 0) {
    return (
      <Card className="shadow-sm border-0">
        <CardBody className="text-center py-5">
          <div
            className="flex flex-col items-center justify-center"
            style={{ minHeight: '50vh' }}
          >
            <div
              className="rounded-full flex items-center justify-center mb-4"
              style={{
                width: 120,
                height: 120,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 10px 40px rgba(102, 126, 234, 0.3)',
              }}
            >
              <Building className="h-12 w-12 text-white" />
            </div>
            <h3 className="font-bold text-foreground mb-3">{t('Snags_no_buildings_title')}</h3>
            <p className="text-muted-foreground mb-4 mx-auto" style={{ maxWidth: 450, fontSize: '1.1rem', lineHeight: 1.6 }}>
              {t('Snags_no_buildings_message')}
            </p>
            <div
              className="flex items-center justify-center px-4 py-3 rounded-lg mb-4"
              style={{ backgroundColor: '#f8f9fa', maxWidth: 500 }}
            >
              <Settings className="h-5 w-5 text-primary mr-3" />
              <span className="text-muted-foreground">{t('Snags_no_buildings_hint')}</span>
              <ArrowRight className="h-4 w-4 text-primary ml-3" />
            </div>
            <div className="flex items-center mt-3" style={{ gap: '2rem' }}>
              <div className="text-center">
                <div
                  className="rounded-full flex items-center justify-center mx-auto mb-2"
                  style={{ width: 40, height: 40, backgroundColor: '#e3f2fd' }}
                >
                  <span className="font-bold text-primary">1</span>
                </div>
                <small className="text-muted-foreground">{t('Snags_step_add_building', 'Add building')}</small>
              </div>
              <div className="text-muted-foreground">→</div>
              <div className="text-center">
                <div
                  className="rounded-full flex items-center justify-center mx-auto mb-2"
                  style={{ width: 40, height: 40, backgroundColor: '#e3f2fd' }}
                >
                  <span className="font-bold text-primary">2</span>
                </div>
                <small className="text-muted-foreground">{t('Snags_step_add_floor', 'Add floors')}</small>
              </div>
              <div className="text-muted-foreground">→</div>
              <div className="text-center">
                <div
                  className="rounded-full flex items-center justify-center mx-auto mb-2"
                  style={{ width: 40, height: 40, backgroundColor: '#e3f2fd' }}
                >
                  <span className="font-bold text-primary">3</span>
                </div>
                <small className="text-muted-foreground">{t('Snags_step_upload_blueprint', 'Upload blueprints')}</small>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
    )
  }

  const fullscreenStyle: React.CSSProperties = isFullscreen
    ? {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1040,
        backgroundColor: '#f8f9fa',
        padding: '1rem',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }
    : {}

  return (
    <div className="blueprint-view" style={fullscreenStyle}>
      {/* Toolbar: Stats + Filters + Search + Actions */}
      <Card className={`shadow-sm border-0 ${isFullscreen ? 'mb-2 shrink-0' : 'mb-0'}`}>
        <div className={`py-2 flex items-center gap-3 flex-wrap ${isFullscreen ? 'px-2' : ''}`}>
          {/* Stats */}
          <div className="flex items-center gap-1">
            <span className="font-bold text-lg">{snagStats.total}</span>
            <span className="text-muted-foreground text-sm">{t('Common_total')}</span>
            {snagStats.visible !== snagStats.total && (
              <span className="ml-1 text-xs text-muted-foreground">
                ({snagStats.visible} {t('Snags_filtered')})
              </span>
            )}
          </div>
          <div className="flex items-center gap-2.5">
            <span className="flex items-center gap-1">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-red-500" />
              <span className="text-xs text-muted-foreground">{snagStats.open}</span>
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" />
              <span className="text-xs text-muted-foreground">{snagStats.inProgress}</span>
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-green-500" />
              <span className="text-xs text-muted-foreground">{snagStats.resolved}</span>
            </span>
          </div>

          {/* Filters + Search */}
          <div className="flex-1 min-w-0">
            <SnagsFilterBar
              filters={filters}
              onFiltersChange={setFilters}
              buildings={buildings}
              infrastructureTypes={infrastructureTypes}
              showBuildingSelector={true}
              showFloorSelector={true}
              showSearch={true}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <ShadcnButton
              variant="outline"
              size="sm"
              className="h-8"
              onClick={() => setIsFullscreen(!isFullscreen)}
              title={isFullscreen ? t('Snags_exit_fullscreen') : t('Snags_fullscreen')}
            >
              {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </ShadcnButton>
            {/* Rendered while already in placement mode too (e.g. the "change pin
                location" flow from SnagModal enters placement mode without going
                through this button) so the Cancel affordance is always reachable,
                even for users who lack canCreate. */}
            {(isPlacementMode || canCreate) && (
              <ShadcnButton
                variant={isPlacementMode ? 'destructive' : 'default'}
                size="sm"
                className="h-8"
                onClick={() => {
                  if (isPlacementMode) {
                    setIsPlacementMode(false)
                    setEditingPinSnagId(null)
                  } else {
                    handleAddSnag()
                  }
                }}
                disabled={!selectedFloor?.blueprintFile}
              >
                {isPlacementMode ? (
                  <>
                    <X className="h-4 w-4" />
                    {t('Common_cancel')}
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4" />
                    {t('Snags_add_snag')}
                  </>
                )}
              </ShadcnButton>
            )}
          </div>
        </div>
      </Card>

      {/* Blueprint Canvas */}
      <div style={isFullscreen ? { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 } : {}}>
        {selectedFloor ? (
          selectedFloor.blueprintFile ? (
            <BlueprintCanvas
              blueprintFile={selectedFloor.blueprintFile}
              snags={visibleSnags}
              selectedSnagId={selectedSnagId}
              isPlacementMode={isPlacementMode}
              onMarkerClick={handleMarkerClick}
              onPlaceMarker={handlePlaceMarker}
              loading={snagsLoading}
              isFullscreen={isFullscreen}
              onOptimizeSuccess={handleUploadSuccess}
              focusPin={focusPin}
            />
          ) : (
            <Card className="shadow-sm border-0">
              <CardBody className="text-center p-4" style={{ minHeight: isFullscreen ? '100%' : '50vh' }}>
                <div
                  className="flex flex-col items-center justify-center h-full rounded"
                  style={{
                    minHeight: isFullscreen ? 'auto' : '40vh',
                    border: '2px dashed #dee2e6',
                    backgroundColor: '#f8f9fa',
                    cursor: canAdmin ? 'pointer' : 'default',
                    transition: 'all 0.2s ease',
                  }}
                  onClick={canAdmin ? () => setShowUploadModal(true) : undefined}
                  onMouseEnter={canAdmin ? (e) => {
                    e.currentTarget.style.borderColor = '#007bff'
                    e.currentTarget.style.backgroundColor = '#f0f7ff'
                  } : undefined}
                  onMouseLeave={canAdmin ? (e) => {
                    e.currentTarget.style.borderColor = '#dee2e6'
                    e.currentTarget.style.backgroundColor = '#f8f9fa'
                  } : undefined}
                >
                  <div
                    className="rounded-full bg-white flex items-center justify-center mb-4 shadow-sm"
                    style={{ width: 100, height: 100 }}
                  >
                    <Upload className="h-10 w-10 text-primary" />
                  </div>
                  <h4 className="font-bold text-foreground mb-2">{t('Snags_no_blueprint_title')}</h4>
                  {canAdmin && (
                    <>
                      <p className="text-muted-foreground mb-3" style={{ maxWidth: 400 }}>
                        {t('Snags_upload_blueprint_drop_hint')}
                      </p>
                      <p className="text-muted-foreground small mb-0">{t('Snags_upload_blueprint_supported')}</p>
                    </>
                  )}
                </div>
              </CardBody>
            </Card>
          )
        ) : (
          <Card className="shadow-sm border-0">
            <CardBody className="text-center py-5" style={{ minHeight: isFullscreen ? '100%' : '50vh' }}>
              <div
                className="flex flex-col items-center justify-center h-full"
                style={{ minHeight: isFullscreen ? 'auto' : '40vh' }}
              >
                <div
                  className="rounded-full bg-muted flex items-center justify-center mb-4"
                  style={{ width: 100, height: 100 }}
                >
                  <Layers className="h-10 w-10 text-muted-foreground opacity-50" />
                </div>
                <h4 className="font-bold text-foreground mb-2">{t('Snags_select_floor_title')}</h4>
                <p className="text-muted-foreground mb-0" style={{ maxWidth: 400 }}>
                  {t('Snags_select_floor')}
                </p>
              </div>
            </CardBody>
          </Card>
        )}
      </div>

      {/* Snag Modal */}
      {showSnagModal && (
        <SnagModal
          isOpen={showSnagModal}
          onClose={handleSnagModalClose}
          snagId={selectedSnagId}
          floorId={filters.floorId}
          initialPosition={newSnagPosition}
          onChangePinLocation={handleChangePinLocation}
          canChangePinLocation={true}
          filteredSnagIds={filteredSnagIds}
          onNavigate={handleNavigateSnag}
          pendingPinCoordinates={pendingPinCoordinates}
          onShowOnBlueprint={handleShowOnBlueprint}
        />
      )}

      {/* Blueprint Upload Modal */}
      {filters.floorId && (
        <BlueprintUploadModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          floorId={filters.floorId}
          floorName={selectedFloor?.name}
          onSuccess={handleUploadSuccess}
        />
      )}
    </div>
  )
}

export default BlueprintView
